import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BuildingIcon, ScanIcon, SearchIcon, UserIcon, UsersIcon, type LucideIcon } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { useAdminSearch } from '@/hooks/useAdmin';
import { useAdminCaseSelection } from '../hooks/useAdminCaseSelection';
import { AdminSearchHit, AdminSearchHitType } from '@/schema/admin';

const HIT_ICONS: Record<AdminSearchHitType, LucideIcon> = {
  PATIENT: UserIcon,
  CLINIC: BuildingIcon,
  ORTHODONTIST: UsersIcon,
  USER: UsersIcon,
  SCAN: ScanIcon,
};

const HIT_LABELS: Record<AdminSearchHitType, string> = {
  PATIENT: 'Patient',
  CLINIC: 'Clinic',
  ORTHODONTIST: 'Orthodontist',
  USER: 'User',
  SCAN: 'Scan',
};

/**
 * One box for the whole platform. Pasting a UUID from a log or a support e-mail resolves
 * straight to the record, which is the fastest path from "a user reported X" to the data.
 */
export function GlobalSearch({ className }: { className?: string }) {
  const [term, setTerm] = useState('');
  const [debounced, setDebounced] = useState('');
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { openCase } = useAdminCaseSelection();

  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(term), 250);
    return () => window.clearTimeout(timer);
  }, [term]);

  useEffect(() => {
    const onPointerDown = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, []);

  const { data: hits = [], isFetching } = useAdminSearch(debounced);

  const goTo = (hit: AdminSearchHit) => {
    setOpen(false);
    setTerm('');
    switch (hit.type) {
      case 'CLINIC':
        navigate(`/admin/clinics/${hit.id}`);
        break;
      case 'PATIENT':
        // Stays inside the console — the clinic app is a different viewport.
        openCase(hit.id);
        break;
      case 'SCAN':
        navigate(`/admin/pipeline?search=${encodeURIComponent(hit.sublabel ?? '')}`);
        break;
      case 'ORTHODONTIST':
      case 'USER':
        navigate('/admin/people');
        break;
    }
  };

  const showResults = open && debounced.trim().length >= 2;

  return (
    <div ref={containerRef} className={cn('relative', className)}>
      <div className="relative">
        <SearchIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-white/35" />
        <Input
          value={term}
          onChange={(event) => {
            setTerm(event.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          placeholder="Search patients, clinics, people or paste an ID"
          className="pl-9"
          aria-label="Global search"
        />
      </div>

      {showResults ? (
        <div className="absolute left-0 right-0 top-full z-[1200] mt-2 max-h-96 overflow-y-auto rounded-xl border border-content-highlight/45 bg-content-dark/95 p-1 shadow-xl backdrop-blur">
          {hits.length === 0 ? (
            <p className="px-3 py-4 text-sm text-white/50">
              {isFetching ? 'Searching…' : 'Nothing matched.'}
            </p>
          ) : (
            hits.map((hit) => {
              const Icon = HIT_ICONS[hit.type];
              return (
                <button
                  key={`${hit.type}-${hit.id}`}
                  type="button"
                  onClick={() => goTo(hit)}
                  className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left transition-colors hover:bg-primary/15"
                >
                  <Icon className="h-4 w-4 shrink-0 text-white/40" />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm text-white">{hit.label}</span>
                    {hit.sublabel ? (
                      <span className="block truncate text-xs text-white/45">{hit.sublabel}</span>
                    ) : null}
                  </span>
                  <span className="shrink-0 text-[10px] font-semibold uppercase tracking-[0.18em] text-white/35">
                    {HIT_LABELS[hit.type]}
                  </span>
                </button>
              );
            })
          )}
        </div>
      ) : null}
    </div>
  );
}
